// Parallel to FeatureSteps.jsx — for Clara DB data (Global/ESI integration domains).

import React, { useEffect, useState, useMemo } from "react";
import {
  SdfFocusPane,
  SdfTab,
  SdfTabGroup,
  SdfLink,
  SdfBadge,
  SdfExpandableBox,
  SdfxPagination,
  SdfSelectSimple,
  SdfSpotIllustration,
  SdfShimmer,
  SdfTooltip,
  SdfIconButton,
  SdfIcon,
  SdfBox,
  SdfSegmentedControl,
} from "@synerg/react-components";
import get from "lodash/get";
import { SearchInput } from "../../../components/SearchInput";
import { HttpMethodsToSdfEnum } from "../../../utility/enums/HttpMethodsToSdfEnum";
import { InputWidthEnum } from "../../../utility/enums/InputWidthEnum";
import { useFetchUseCaseApisQuery, useFetchUseCaseApisSorQuery, useFetchOperationDetailsQuery, useFetchRelatedGuidesQuery } from "../../../store";
import { BusyPageIndicator } from "../../../components/BusyPageIndicator";
import { loadSample } from "../../../store/apis/swaggerApi";
import "./ClaraFeatureSteps.css";
import { HTTP_METHODS, ROWS_PER_PAGE_OPTIONS, REQUEST_SUB_TABS, DETAIL_TABS } from "./ClaraFeatureSteps.constants";

// ── Slug → Guide Title helpers ────────────────────────────────────────────────
// Known words that must keep exact casing regardless of their position in a slug.
const SLUG_WORD_MAP = {
  adp:       'ADP',
  api:       'API',
  apis:      'APIs',
  hcm:       'HCM',
  hr:        'HR',
  ihcm:      'iHCM',
  wfn:       'WFN',
  run:       'RUN',
  sso:       'SSO',
  oauth:     'OAuth',
  json:      'JSON',
  xml:       'XML',
  url:       'URL',
  id:        'ID',
  ids:       'IDs',
  uk:        'UK',
  nl:        'NL',
  mc:        'MC',
  sor:       'SOR',
  esi:       'ESI',
  expert:    'eXpert',
  ui:          'UI',
  ux:          'UX',
  saml:        'SAML',
  mtls:        'mTLS',
  tls:         'TLS',
  ssl:         'SSL',
  advancedpay: 'AdvancedPay',
  oneservice:  'OneService',
  globalview:  'GlobalView',
  adplink:     'ADP Link',
  adpexpert:   'ADP eXpert',
  decidium:    'DECIDIUM',
  mamagement:  'Management',
  v1:          'v1',
  v2:        'v2',
  v3:        'v3',
  v4:        'v4',
};
// Prepositions / conjunctions that stay lowercase unless they are the first word.
const LOWERCASE_WORDS = new Set(['a', 'an', 'the', 'and', 'or', 'but', 'for',
  'nor', 'on', 'at', 'to', 'by', 'in', 'of', 'up', 'as', 'is', 'its', 'via']);

function formatSlug(slug) {
  // Pre-process multi-dash runs before splitting on '-':
  //   ---  (3+ dashes) → '-\x00-'  so the sentinel '\x00' becomes a standalone
  //                       word that maps to '-', giving ' - ' after join(' ')
  //   --   (2 dashes)  → empty string preserved, giving two spaces after join(' ')
  return slug
    .replace(/---+/g, '-\x00-')
    .split('-')
    .map((word, idx) => {
      if (word === '\x00') return '-';  // sentinel → literal dash  (' - ' after join)
      if (!word) return '';             // from --   → extra space   ('  '  after join)
      const lower = word.toLowerCase();
      if (SLUG_WORD_MAP[lower] !== undefined) return SLUG_WORD_MAP[lower];
      if (idx > 0 && LOWERCASE_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

// Extract the last meaningful path segment from an http URL.
// Strips (in order): query strings (?chapter=7), hash fragments (#section),
// and /chapter/N path suffixes before taking the final segment.
function extractGuideSlug(url) {
  if (!url?.startsWith('http')) return null;
  const withoutQuery    = url.split('?')[0];
  const withoutFragment = withoutQuery.split('#')[0];
  const withoutChapter  = withoutFragment.replace(/\/chapter\/.*$/, '');
  return withoutChapter.split('/').filter(Boolean).pop() || null;
}

// Normalises any string to lowercase-hyphen-only form so lookups are resilient to:
//   - mixed/upper case            : "Organization-Management" → "organization-management"
//   - underscores                 : "worker_management"       → "worker-management"
//   - special chars (®, ©, –, …) : "ADP® iHCM"              → "adp-ihcm"
//   - typo-adjacent multi-dashes  : "guide-for--adp"          → "guide-for-adp"
//   - URL-encoded chars           : "%20"                     → (already decoded by browser)
function normalizeSlug(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')  // collapse any run of non-alphanumeric chars to a single hyphen
    .replace(/^-+|-+$/g, '');     // strip leading / trailing hyphens
}

// Skeleton shown while the API list is loading — mimics the shape of the real content.
function ApiListSkeleton() {
  return (
    <div className="animate-pulse">
      {/* Method tabs skeleton */}
      <div className="flex gap-3 mb-6">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-8 w-20 bg-action-default-100 rounded" />
        ))}
      </div>
      {/* Row skeletons */}
      <div className="flex flex-col gap-3 px-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-14 bg-action-default-100 rounded-lg" />
        ))}
      </div>
    </div>
  );
}

// Group label: first path segment + adc.title
// Short segments (≤3 chars) treated as abbreviations: hr→HR, api→API
// Longer segments are Title Cased: core→Core, payroll→Payroll
function getApiGroup(apiPath, apiSpecTitle) {
  const formatSegment = (s) =>
    s.length <= 3 ? s.toUpperCase() : s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  const prefix = apiPath ? formatSegment(apiPath.split('/').filter(Boolean)[0] || '') : '';
  if (prefix && apiSpecTitle) return `${prefix} > ${apiSpecTitle}`;
  if (apiSpecTitle) return apiSpecTitle;
  return prefix;
}

// Waypoint copy button with "Copied" tooltip on click.
function CopyButton({ getText }) {
  return (
    <SdfTooltip trigger="click" state="info">
      <span slot="tooltip-target" onClick={() => navigator.clipboard.writeText(getText())}>
        <SdfIconButton shape="rounded" style={{ color: '#ffffff' }}>
          <SdfIcon icon="action-copy" className="cursor-pointer" />
        </SdfIconButton>
      </span>
      Copied
    </SdfTooltip>
  );
}

function DownloadButton({ getContent, filename = 'response.json' }) {
  const handleClick = () => {
    const blob = new Blob([getContent()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <SdfTooltip trigger="click" state="info">
      <span slot="tooltip-target" onClick={handleClick}>
        <SdfIconButton shape="rounded" style={{ color: '#ffffff' }}>
          <SdfIcon icon="action-download" className="cursor-pointer" />
        </SdfIconButton>
      </span>
      Downloaded
    </SdfTooltip>
  );
}

function ExpandButton({ getContent }) {
  const [visible, setVisible] = useState(false);
  return (
    <>
      <SdfTooltip trigger="hover" state="info">
        <span slot="tooltip-target" onClick={() => setVisible(true)}>
          <SdfIconButton shape="rounded" style={{ color: '#ffffff' }}>
            <SdfIcon icon="action-maximize" className="cursor-pointer" />
          </SdfIconButton>
        </span>
        Expand
      </SdfTooltip>
      <SdfFocusPane
        visible={visible}
        size="xl"
        hide-accept-button=""
        hide-dismiss-button=""
        pane-type="floating"
        closeable={true}
        dismissOnClickOutside={false}
        onSdfDismiss={() => setVisible(false)}
        spacing="none"
      >
        <SdfBox shape="squared" className="h-xl overflow-auto bg-action-default-900 font-mono text-sm">
          <pre><code className="text-inverse-secondary">{getContent()}</code></pre>
        </SdfBox>
      </SdfFocusPane>
    </>
  );
}

// Renders an already-loaded JSON string in the dark code block (copy + download).
function InlineCodeBlock({ body, filename = 'response.json', mediaType, showExpand = false }) {
  const isEmpty = !body || body.trim() === '{}';
  return (
    <>
      <p className="cfs-scenario-media">
        <span className="cfs-scenario-media-label">Media Type: </span>{mediaType}
      </p>
      <div className="cfs-code-wrap">
        {!isEmpty && (
          <div className="cfs-code-actions">
            <CopyButton getText={() => body} />
            <DownloadButton getContent={() => body} filename={filename} />
            {showExpand && <ExpandButton getContent={() => body} />}
          </div>
        )}
        <pre className="cfs-pre cfs-pre--bounded">
          {isEmpty ? '{}' : body}
        </pre>
      </div>
    </>
  );
}

// Fetches and renders the JSON body for a single response scenario.
function ResponseScenarioBody({ link, body: bodyProp = null, filename = 'response.json', mediaType, showExpand = false }) {
  const [body, setBody] = useState(bodyProp);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (bodyProp !== null) { setBody(bodyProp); return; }
    if (!link) return;
    setLoading(true);
    loadSample(link)
      .then((data) => setBody(typeof data === "string" ? data : JSON.stringify(data, null, 2)))
      .catch(() => setBody(null))
      .finally(() => setLoading(false));
  }, [link, bodyProp]);

  if (loading) return <SdfShimmer className="w-full" rows="3" />;

  const displayBody = body ?? "{}";
  const isEmpty = displayBody.trim() === '{}';

  return (
    <>
      <p className="cfs-scenario-media">
        <span className="cfs-scenario-media-label">Media Type: </span>{mediaType}
      </p>
      <div className="cfs-code-wrap">
        {!isEmpty && (
          <div className="cfs-code-actions">
            <CopyButton getText={() => displayBody} />
            <DownloadButton getContent={() => displayBody} filename={filename} />
            {showExpand && <ExpandButton getContent={() => displayBody} />}
          </div>
        )}
        <pre className="cfs-pre cfs-pre--bounded">
          {displayBody}
        </pre>
      </div>
    </>
  );
}

// Gear + pencil illustration used for empty-state panels — matches IAT design
function GearEmptyIllustration() {
  return (
    <svg width="64" height="56" viewBox="0 0 64 56" fill="none" aria-hidden="true">
      <circle cx="26" cy="26" r="11" stroke="#D1D5DB" strokeWidth="2.5" fill="#F3F4F6"/>
      <circle cx="26" cy="26" r="4.5" stroke="#D1D5DB" strokeWidth="2" fill="white"/>
      <rect x="23.5" y="9" width="5" height="8" rx="1.5" fill="#D1D5DB"/>
      <rect x="23.5" y="35" width="5" height="8" rx="1.5" fill="#D1D5DB"/>
      <rect x="9" y="23.5" width="8" height="5" rx="1.5" fill="#D1D5DB"/>
      <rect x="35" y="23.5" width="8" height="5" rx="1.5" fill="#D1D5DB"/>
      <g transform="rotate(45 26 26)">
        <rect x="23.5" y="9" width="5" height="8" rx="1.5" fill="#D1D5DB"/>
        <rect x="23.5" y="35" width="5" height="8" rx="1.5" fill="#D1D5DB"/>
        <rect x="9" y="23.5" width="8" height="5" rx="1.5" fill="#D1D5DB"/>
        <rect x="35" y="23.5" width="8" height="5" rx="1.5" fill="#D1D5DB"/>
      </g>
      <g transform="translate(48 40) rotate(-45)">
        <rect x="-3" y="-12" width="6" height="16" rx="1.5" fill="#D1D5DB"/>
        <path d="M-3 4L0 9L3 4Z" fill="#D1D5DB"/>
      </g>
    </svg>
  );
}

const EXAMPLE_CODE_TYPES = [
  { label: 'Request', value: 'request' },
  { label: 'Response', value: 'response' },
];

function ExamplesPanel({ scenarios, step }) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [codeType, setCodeType] = useState('request');
  const [selectedCode, setSelectedCode] = useState(null);

  // Each scenario has its own nested requestScenarios + responseScenarios
  const scenario = scenarios[selectedIdx];

  // Response codes come from the selected scenario's responseScenarios
  const responseCodes = useMemo(() => {
    const seen = new Set();
    return (scenario?.responseScenarios ?? [])
      .map((s) => s.code)
      .filter((c) => c && !seen.has(c) && seen.add(c));
  }, [scenario]);

  // Auto-select first response code when scenario changes
  useEffect(() => {
    setSelectedCode(responseCodes[0] ?? null);
  }, [selectedIdx, scenario]);

  // Active response scenario within the selected scenario's responseScenarios
  const activeResponseScenario = (scenario?.responseScenarios ?? []).find((s) => s.code === selectedCode);

  // Request info from the selected scenario's requestScenarios
  const activeRequestScenario = scenario?.requestScenarios?.[0];

  const filename = `${(step.httpMethod || 'get').toLowerCase()}_${(step.apiPath || '').replace(/\//g, '_').replace(/^_/, '')}_example_${selectedIdx + 1}.json`;

  return (
    <div className="cfs-examples-layout mt-2">
      {/* Left scenario nav — all top-level scenarios */}
      <div className="cfs-examples-nav">
        <SdfTabGroup
          vertical
          size="small"
          onSdfChange={(e) => { setSelectedIdx(Number(e.detail.value)); setCodeType('request'); }}
        >
          {scenarios.map((s, i) => (
            <SdfTab key={i} value={String(i)} active={selectedIdx === i}>
              {s.name || `Scenario ${i + 1}`}
            </SdfTab>
          ))}
        </SdfTabGroup>
      </div>

      {/* Right content */}
      <div className="cfs-examples-content">
        <p className="text-heading-05 font-bold mb-2">{scenario?.name || `Scenario ${selectedIdx + 1}`}</p>

        {scenario?.description && (
          <div className="mb-4">
            <p className="text-heading-04 font-bold mb-1">Description</p>
            <p className="text-body-02">{scenario.description}</p>
          </div>
        )}

        <p className="text-heading-04 font-bold mt-4 mb-3">Code sample(s)</p>

        <div className="flex justify-center mb-4 mt-6">
          <SdfSegmentedControl
            key={`seg-${selectedIdx}`}
            items={EXAMPLE_CODE_TYPES}
            selectedIndex={EXAMPLE_CODE_TYPES.findIndex((t) => t.value === codeType)}
            onSdfChange={(e) => {
              const idx = e.target?.selectedIndex ?? Number(e.detail?.selectedIndex ?? e.detail?.value);
              const resolved = EXAMPLE_CODE_TYPES[idx]?.value;
              if (resolved) setCodeType(resolved);
            }}
          />
        </div>

        {codeType === 'request' && (
          // requestScenarios only carry mediaType/scenarioIndex — no body link available
          <p className="text-tertiary text-sm mt-4">No request sample available.</p>
        )}

        {codeType === 'response' && (
          <>
            {responseCodes.length > 0 && (
              <SdfTabGroup
                key={`res-codes-${selectedIdx}`}
                size="small"
                className="mb-3"
                onSdfChange={(e) => {
                  const idx = Number(e.detail.value);
                  setSelectedCode(responseCodes[idx] ?? e.detail.value);
                }}
              >
                {responseCodes.map((code) => (
                  <SdfTab key={code} value={code} active={code === selectedCode}>
                    {code}
                  </SdfTab>
                ))}
              </SdfTabGroup>
            )}
            {activeResponseScenario?.name && (
              <p className="cfs-scenario-name">{activeResponseScenario.name}</p>
            )}
            <ResponseScenarioBody
              link={activeResponseScenario?.link}
              mediaType={activeResponseScenario?.mediaType}
              filename={`${(step.httpMethod || 'get').toLowerCase()}_${(step.apiPath || '').replace(/\//g, '_').replace(/^_/, '')}_response_${activeResponseScenario?.code}.json`}
              showExpand={true}
            />
          </>
        )}
      </div>
    </div>
  );
}

function ApiItemDetails({ step, isSorFetching, sorData = [], guideTitleMap = new Map() }) {
  const [selectedTab, setSelectedTab] = useState("request");
  const [selectedRequestSubTab, setSelectedRequestSubTab] = useState("headers");
  const [selectedResponseCode, setSelectedResponseCode] = useState(null);
  const [selectedGuideProduct, setSelectedGuideProduct] = useState('All');

  // Filter SOR rows matching this API's uri + method from the already-fetched sorData
  const apiSorEntries = useMemo(() => (sorData ?? []).filter(
    (row) =>
      row.uri === step.apiPath &&
      (row.method ?? 'GET').toUpperCase() === (step.httpMethod ?? 'GET').toUpperCase()
  ), [sorData, step.apiPath, step.httpMethod]);

  // Dropdown options — only SORs that have an actual http guide URL for this API.
  const sorProductOptions = useMemo(() => {
    const names = [
      ...new Set(
        apiSorEntries
          .filter((r) => r.relatedDocGuideUrl?.startsWith('http') && r.sorName)
          .map((r) => r.sorName)
      ),
    ];
    return [{ label: 'All', value: 'All' }, ...names.map((n) => ({ label: n, value: n }))];
  }, [apiSorEntries]);

  // Entries visible under the guides tab.
  // Only entries with actual http guide URLs are shown — swagger spec paths are not
  // guide links and would render as unhelpful plain text.
  // In "All" mode, deduplicate by NORMALISED SLUG (not exact URL) so that two SORs
  // pointing to the same guide via different base paths (e.g. /guides/api-guides/X
  // and /articles/guides/X) are collapsed into a single entry.
  const filteredSorGuides = useMemo(() => {
    if (selectedGuideProduct !== 'All') {
      return apiSorEntries.filter(
        (r) => r.sorName === selectedGuideProduct && r.relatedDocGuideUrl?.startsWith('http')
      );
    }
    const seen = new Set();
    return apiSorEntries.filter((r) => {
      if (!r.relatedDocGuideUrl?.startsWith('http')) return false;
      const slug = extractGuideSlug(r.relatedDocGuideUrl);
      const key = slug ? normalizeSlug(slug) : r.relatedDocGuideUrl;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [apiSorEntries, selectedGuideProduct]);

  // Fetch full operation details — fires on mount (component only mounts when the row is expanded).
  const {
    data: opData,
    isLoading: isOpLoading,
  } = useFetchOperationDetailsQuery(
    { method: step.httpMethod, path: step.apiPath },
    { skip: !step.httpMethod || !step.apiPath }
  );

  // Pick first response scenario as default when data arrives.
  useEffect(() => {
    const scenarios = opData?.response?.responseScenarios;
    if (scenarios?.length && !selectedResponseCode) {
      setSelectedResponseCode(scenarios[0].code);
    }
  }, [opData]);

  // ── Request tab content ────────────────────────────────────────────────────
  const requestContent = isOpLoading ? (
    <SdfShimmer className="w-full" rows="4" />
  ) : (
    <div className="mt-2">
      {/* Sub-tab bar — Headers / Parameters / cURL */}
      <SdfTabGroup
        size="small"
        className="mb-4"
        onSdfChange={(e) => setSelectedRequestSubTab(e.detail.value)}
      >
        {REQUEST_SUB_TABS.map((t) => (
          <SdfTab key={t.value} value={t.value} active={selectedRequestSubTab === t.value}>
            {t.label}
          </SdfTab>
        ))}
      </SdfTabGroup>

      {/* Headers */}
      {selectedRequestSubTab === "headers" && (
        <table className="cfs-table">
          <thead>
            <tr>
              <th className="cfs-th-first cfs-col-30">Key</th>
              <th className="cfs-th-mid cfs-col-40">Value</th>
              <th className="cfs-th-last">Data format</th>
            </tr>
          </thead>
          <tbody>
            {(opData?.request?.header ?? []).map((h, i) => (
              <tr key={i}>
                <td className="cfs-td-first">{h.key}</td>
                <td className="cfs-td-mid cfs-td-mono">{h.value}</td>
                <td className="cfs-td-last">{h.dataFormat}</td>
              </tr>
            ))}
            {!opData?.request?.header?.length && (
              <tr><td colSpan={3} className="cfs-td-no-data">No headers defined.</td></tr>
            )}
          </tbody>
        </table>
      )}

      {/* Parameters */}
      {selectedRequestSubTab === "parameters" && (() => {
        const required = opData?.request?.parameters?.required ?? [];
        const optional = opData?.request?.parameters?.optional ?? [];
        const paramTable = (rows) => (
          <table className="cfs-table">
            <thead>
              <tr>
                <th className="cfs-th-first cfs-col-22">Name</th>
                <th className="cfs-th-mid cfs-col-15">Type</th>
                <th className="cfs-th-mid cfs-col-15">Format</th>
                <th className="cfs-th-last">Description</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => (
                <tr key={i}>
                  <td className="cfs-td-first cfs-td-mono">{p.name}</td>
                  <td className="cfs-td-mid cfs-td-dim">{p.type}</td>
                  <td className="cfs-td-mid cfs-td-dim">{p.format}</td>
                  <td className="cfs-td-last">{p.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
        return (
          <div>
            <p className="cfs-param-label">Required</p>
            {required.length ? paramTable(required) : (
              <div className="mt-10 flex flex-col items-center mb-10">
                <SdfSpotIllustration illustrationName="empty-skills" size="md" />
                <div className="mt-10 text-center">
                  <div className="text-heading-04 font-bold text-tertiary">No required parameters provided</div>
                </div>
              </div>
            )}
            <p className="cfs-param-label">Optional</p>
            {optional.length ? paramTable(optional) : (
              <div className="mt-10 flex flex-col items-center mb-10">
                <SdfSpotIllustration illustrationName="empty-skills" size="md" />
                <div className="mt-10 text-center">
                  <div className="text-heading-04 font-bold text-tertiary">No optional parameters provided</div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* cURL */}
      {selectedRequestSubTab === "curl" && (() => {
        const raw = opData?.request?.preview ?? "";
        // Format cURL: split on flag boundaries so each option is on its own indented line
        const formatCurl = (str) => {
          if (!str) return "No cURL preview available.";
          // Insert newline+indent before every flag (e.g. -H, -d, -X, --data, --header, etc.)
          return str
            .replace(/\s+(-{1,2}[A-Za-z])/g, (_, flag) => `\\\n  ${flag}`)
            .trim();
        };
        const formatted = formatCurl(raw);
        return (
          <div className="cfs-code-wrap">
            <div className="cfs-code-actions">
              <CopyButton getText={() => formatted} />
            </div>
            <pre className="cfs-pre cfs-pre--curl">
              {formatted}
            </pre>
          </div>
        );
      })()}
    </div>
  );

  // ── Response(s) tab content ────────────────────────────────────────────────
  const scenarios = opData?.response?.responseScenarios ?? [];
  const activeScenario = scenarios.find((s) => s.code === selectedResponseCode);

  const responsesContent = isOpLoading ? (
    <SdfShimmer className="w-full" rows="4" />
  ) : (
    <div className="mt-2">
      {scenarios.length ? (
        <>
          {/* Status code tabs — Waypoint SdfTabGroup horizontal */}
          <SdfTabGroup
            size="small"
            onSdfChange={(e) => {
              const idx = Number(e.detail.value);
              const code = scenarios[idx]?.code ?? e.detail.value;
              setSelectedResponseCode(code);
            }}
            className="mb-3"
          >
            {scenarios.map((s) => (
              <SdfTab key={s.code} value={s.code} active={s.code === selectedResponseCode}>
                {s.code}
              </SdfTab>
            ))}
          </SdfTabGroup>
          {activeScenario && (
            <div>
              <p className="cfs-scenario-name">{activeScenario.name}</p>
              {activeScenario.link ? (
                <ResponseScenarioBody link={activeScenario.link} mediaType={activeScenario.mediaType} filename={`${(step.httpMethod || 'get').toLowerCase()}_${(step.apiPath || '').replace(/\//g, '_').replace(/^_/, '')}_response_${activeScenario.code}.json`} showExpand={true} />
              ) : opData?.responseBodies?.[activeScenario.code] ? (
                <InlineCodeBlock body={JSON.stringify(opData.responseBodies[activeScenario.code], null, 2)} mediaType={activeScenario.mediaType} filename={`${(step.httpMethod || 'get').toLowerCase()}_${(step.apiPath || '').replace(/\//g, '_').replace(/^_/, '')}_response_${activeScenario.code}.json`} showExpand={true} />
              ) : (
                <InlineCodeBlock body="{}" mediaType={activeScenario.mediaType} filename={`${(step.httpMethod || 'get').toLowerCase()}_${(step.apiPath || '').replace(/\//g, '_').replace(/^_/, '')}_response_${activeScenario.code}.json`} showExpand={true} />
              )}
            </div>
          )}
        </>
      ) : (
        <div className="mt-24 flex flex-col items-center mb-16">
          <SdfSpotIllustration illustrationName="empty-skills" size="md" />
          <div className="mt-10 text-center">
            <div className="text-heading-04 font-bold text-tertiary">No responses provided</div>
          </div>
        </div>
      )}
    </div>
  );

  // ── Example(s) tab content ─────────────────────────────────────────────────
  const exampleScenarios = opData?.example?.scenarios ?? [];
  const examplesContent = isOpLoading ? (
    <SdfShimmer className="w-full" rows="4" />
  ) : exampleScenarios.length ? (
    <ExamplesPanel scenarios={exampleScenarios} step={step} />
  ) : (
    <div>
      <div className="text-heading-03 font-bold">Example scenarios</div>
      <div className="text-body-02 mb-8">Select an example from the list below to view description, code samples and parameters used.</div>
      <div className="mt-24 flex flex-col items-center mb-16">
        <SdfSpotIllustration illustrationName="empty-skills" size="md" />
        <div className="mt-10 text-center">
          <div className="text-heading-04 font-bold text-tertiary">No examples provided</div>
        </div>
      </div>
    </div>
  );

  // ── Related Guides tab ─────────────────────────────────────────────────────
  // Guide entries come from the SOR API (/clara/usecases/:id/sor) already
  // fetched by ClaraFeatureSteps and passed down via `sorData`.
  // apiSorEntries is filtered to the current API (uri + method) above.
  const guidesContent = isSorFetching ? (
    <p className="text-tertiary text-sm">Loading…</p>
  ) : (
    <div className="cfs-guides-wrap">
      {filteredSorGuides.length > 0 ? (
        <div className="cfs-guides-list">
          {filteredSorGuides.map((entry, i) => {
            const url = entry.relatedDocGuideUrl;
            const slug = extractGuideSlug(url);
            // Resolution order:
            // 1. Exact slug match     → guideDocId stored verbatim in ES
            // 2. Normalised match     → catches case/underscore/special-char/typo drift
            // 3. formatSlug fallback  → only for guides genuinely absent from ES
            // 4. sorName              → absolute last resort
            const guideTitle =
              (slug && (guideTitleMap.get(slug) || guideTitleMap.get(normalizeSlug(slug)))) ||
              (slug ? formatSlug(slug) : null) ||
              entry.sorName;
            return (
              <div key={i} className="flex items-center gap-2 py-1">
                <SdfIcon icon="external-link" className="shrink-0" style={{ color: '#0563C1' }} />
                <SdfLink href={url} target="_blank" className="cfs-guide-link">
                  {guideTitle}
                </SdfLink>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-24 flex flex-col items-center mb-16">
          <SdfSpotIllustration illustrationName="empty-skills" size="md" />
          <div className="mt-10 text-center">
            <div className="text-heading-04 font-bold text-tertiary">No related guides available</div>
          </div>
        </div>
      )}
    </div>
  );

  const detailContent = {
    request: requestContent,
    responses: responsesContent,
    examples: examplesContent,
    guides: guidesContent,
  };

  const activeTabLabel = DETAIL_TABS.find(t => t.value === selectedTab)?.label ?? '';

  return (
    <div>
      {/* Description — dedicated space always reserved; text shown after load if available */}
      <div className="cfs-objective-wrap">
        {!isOpLoading && opData?.description && (
          <p className="cfs-objective-text">{opData.description}</p>
        )}
      </div>
      {/* Tab layout: left vertical nav + right content */}
      {isOpLoading ? (
        <SdfShimmer className="w-full mt-2" rows="4" />
      ) : (
      <div className="cfs-detail-layout">
        {/* Left nav — stacked Waypoint SdfTab items */}
        <div className="cfs-detail-nav">
          <SdfTabGroup
            vertical
            size="small"
            onSdfChange={(e) => setSelectedTab(e.detail.value)}
          >
            {DETAIL_TABS.map((tab) => (
              <SdfTab
                key={tab.value}
                value={tab.value}
                active={selectedTab === tab.value}
              >
                {tab.label}
              </SdfTab>
            ))}
          </SdfTabGroup>
        </div>
        {/* Right content panel */}
        <div className="cfs-detail-content">
          {/* Heading row — shows ADP Product filter inline when on guides tab */}
          <div className="cfs-detail-heading-row">
            <div className="text-heading-05 font-bold" style={{margin: '0 0 8px'}}>{activeTabLabel}</div>
            {selectedTab === 'guides' && apiSorEntries.length > 1 && (
              <div className="cfs-product-filter">
                <span className="cfs-product-label">ADP Product</span>
                <SdfSelectSimple
                  size="sm"
                  inputWidth_md="lg"
                  items={sorProductOptions}
                  value={sorProductOptions.find((o) => o.value === selectedGuideProduct)}
                  onSdfChange={(e) => setSelectedGuideProduct(e.detail.value ?? 'All')}
                />
              </div>
            )}
          </div>
          {detailContent[selectedTab]}
        </div>
      </div>
      )}
    </div>
  );
}

function ClaraFeatureSteps(props) {
  const [allSteps, setAllSteps] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMethod, setSelectedMethod] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Tracks which rows have ever been opened — key format: "METHOD::apiPath".
  // ApiItemDetails (and its useFetchOperationDetailsQuery) only mounts once the
  // row is first opened, preventing bulk API calls at panel-open time.
  const [expandedKeys, setExpandedKeys] = useState(new Set());

  const { handleOnHide, show, selectedFeatureItem } = props;

  const useCaseId = get(selectedFeatureItem, 'useCaseId');

  // Fetch core API list — fires as soon as panel opens.
  const {
    data: apisData,
    isFetching: isApiFetching,
  } = useFetchUseCaseApisQuery(useCaseId, { skip: !useCaseId || !show });

  // Fetch SOR enrichment in parallel — same skip condition.
  // Provides sorApiStatus, deliveryDate, relatedDocGuideUrl per (uri, method).
  const {
    data: sorData,
    isFetching: isSorFetching,
  } = useFetchUseCaseApisSorQuery(useCaseId, { skip: !useCaseId || !show });

  // Fetch guide titles at panel level — fires in parallel with SOR/API fetches the moment
  // the panel opens, so guideTitleMap is ready before the first row is ever expanded.
  // Shared across all ApiItemDetails rows — computed once, not once per expanded row.
  const { data: relatedGuides = [] } = useFetchRelatedGuidesQuery();
  const guideTitleMap = useMemo(() => {
    const map = new Map();
    for (const g of relatedGuides) {
      if (!g.guideDocId) continue;
      map.set(g.guideDocId, g.guideTitle);               // exact key — always wins
      const norm = normalizeSlug(g.guideDocId);
      if (!map.has(norm)) map.set(norm, g.guideTitle);   // normalised key — catches drift
    }
    return map;
  }, [relatedGuides]);

  // sorMap: uri::METHOD → first SOR row (first by sor_name ORDER in SQL)
  const sorMap = useMemo(() => {
    const map = new Map();
    for (const row of sorData ?? []) {
      const key = `${row.uri}::${(row.method ?? 'GET').toUpperCase()}`;
      if (!map.has(key)) map.set(key, row);
    }
    return map;
  }, [sorData]);

  // Enrich a core step with SOR data at render time (no re-render needed when SOR arrives).
  const enrichStep = (step) => {
    const sor = sorMap.get(`${step.apiPath}::${step.httpMethod}`) ?? {};
    return {
      ...step,
      sorApiStatus: sor.sorApiStatus ?? null,
      deliveryDate: sor.deliveryDate ?? null,
      relatedDocumentation: {
        relatedDocGuideTitle: step.integrationObjective,
        relatedDocGuideUrl:   sor.relatedDocGuideUrl ?? '',
      },
    };
  };

  useEffect(() => {
    if (!apisData) return;
    // apisData is now a flat array of core API objects (no SOR grouping).
    const steps = apisData.map((api) => ({
      integrationObjective:      api.openapiSummary,
      relatedAvailabilityStatus: api.specStatusCode ?? '',
      httpMethod:                api.method,
      apiPath:                   api.uri,
      apiSpecTitle:              api.canonicalTitle ?? '',
      // SOR fields start empty; enrichStep() fills them at render time from sorMap.
      sorApiStatus:  null,
      deliveryDate:  null,
      relatedDocumentation: { relatedDocGuideTitle: api.openapiSummary, relatedDocGuideUrl: '' },
    }));
    setAllSteps(steps);
    setSelectedMethod("ALL");
    setSearchQuery("");
    setCurrentPage(1);
  }, [apisData]);

  // Reset to page 1 whenever filters or page size change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMethod, searchQuery, pageSize]);

  // Normalise httpMethod — guard against null/undefined coming from server
  const getMethod = (step) => (step.httpMethod || "GET").toUpperCase();

  // Pre-compute per-method counts (respecting active search query)
  const countByMethod = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const searchFiltered = allSteps.filter((step) => {
      if (!q) return true;
      return (
        (step.integrationObjective || "").toLowerCase().includes(q) ||
        (step.apiPath || "").toLowerCase().includes(q)
      );
    });
    const counts = { ALL: searchFiltered.length };
    for (const m of ["GET", "POST", "PUT", "DELETE"]) {
      counts[m] = searchFiltered.filter((s) => getMethod(s) === m).length;
    }
    return counts;
  }, [allSteps, searchQuery]);

  const filteredSteps = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return allSteps.filter((step) => {
      const methodMatch = selectedMethod === "ALL" || getMethod(step) === selectedMethod;
      if (!methodMatch) return false;
      if (!q) return true;
      return (
        (step.integrationObjective || "").toLowerCase().includes(q) ||
        (step.apiPath || "").toLowerCase().includes(q)
      );
    });
  }, [allSteps, searchQuery, selectedMethod]);

  const totalPages = Math.ceil(filteredSteps.length / pageSize);
  const pagedSteps = filteredSteps.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleExpandOpen = (method, apiPath) => {
    console.log('[SdfExpandableBox] opened — method:', method, '| path:', apiPath);

    // Adding the key mounts ApiItemDetails for this row, which triggers
    // useFetchOperationDetailsQuery and feeds data into the request/response tabs.
    setExpandedKeys((prev) => new Set(prev).add(`${method}::${apiPath}`));
  };

  return (
    <div>
      <SdfFocusPane
        hide-dismiss-button=""
        hide-accept-button=""
        closeable={true}
        visible={show}
        onSdfDismiss={() => handleOnHide()}
        size="xl"
        spacing="default"
        dismissOnClickOutside={true}
        className="cfs-focus-pane"
        heading="Associated APIs"
        style={{ minHeight: '70vh' }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-heading-06 font-black m-0">
              {get(selectedFeatureItem, "iconTitle")} APIs
            </h2>
          </div>
          <div className="w-1/2">
            <SearchInput
              value={searchQuery}
              onSdfChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              label="Search"
              inputMaxWidth="max-w-full"
            />
          </div>
        </div>

        {isApiFetching ? (
          <SdfShimmer className="w-full" rows="4" />
        ) : (
          <>
        {/* HTTP method filter tabs with counts */}
        <SdfTabGroup className="mb-6">
          {HTTP_METHODS.map((method) => (
            <SdfTab
              key={method}
              active={selectedMethod === method}
              onClick={() => setSelectedMethod(method)}
            >
              {method} ({countByMethod[method] ?? 0})
            </SdfTab>
          ))}
        </SdfTabGroup>

        {/* API list — grouped by HTTP method sections */}
        <div className="flex flex-col gap-6 px-4 min-h-[70vh]">
          {selectedMethod === "ALL" ? (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-heading-04 font-black">ALL</span>
                <div className="flex-1 border-t border-action-default opacity-30" />
              </div>
              {pagedSteps.length === 0 ? (
                <p className="text-tertiary text-sm py-2 pl-2">No matching Allowed APIs found</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {(() => {
                    const items = [];
                    let lastGroup = null;
                    pagedSteps.forEach((step, index) => {
                      const method = getMethod(step);
                      const group = getApiGroup(step.apiPath, step.apiSpecTitle);
                      if (group !== lastGroup) {
                        lastGroup = group;
                        if (group) {
                          const [prefix, ...rest] = group.split(' > ');
                          const suffix = rest.join(' > ');
                          items.push(
                            <div key={`group-${group}-${index}`} className="flex items-center gap-2 mt-4 mb-1">
                              <span className="text-heading-02 text-default">
                                <span className="font-black">{prefix}</span>
                                {suffix && <span className="font-normal"> &gt; {suffix}</span>}
                              </span>
                              <div className="flex-1 border-t border-action-default opacity-30" />
                            </div>
                          );
                        }
                      }
                      items.push(
                        <SdfExpandableBox
                          key={`${method}-${step.apiPath}-${index}`}
                          variant="shadowed"
                          triggerPlacement="after"
                          onSdfOpen={() => handleExpandOpen(method, step.apiPath)}
                        >
                          <div slot="header" className="flex items-center w-full">
                            <SdfBadge status={HttpMethodsToSdfEnum[method] ?? "default"}>
                              {method}
                            </SdfBadge>
                            <div className="text-heading-04 font-bold ml-4 overflow-hidden truncate">
                              {step.apiPath || step.integrationObjective || "—"}
                            </div>
                          </div>
                          {expandedKeys.has(`${method}::${step.apiPath}`) && (
                            <ApiItemDetails step={enrichStep({ ...step, httpMethod: method })} isSorFetching={isSorFetching} sorData={sorData} guideTitleMap={guideTitleMap} />
                          )}
                        </SdfExpandableBox>
                      );
                    });
                    return items;
                  })()}
                </div>
              )}
            </div>
          ) : (
            [selectedMethod].map((method) => {
              const methodSteps = pagedSteps.filter((step) => getMethod(step) === method);
              return (
                <div key={method}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-heading-04 font-black">{method}</span>
                    <div className="flex-1 border-t border-action-default opacity-30" />
                  </div>
                  {methodSteps.length === 0 ? (
                    <p className="text-tertiary text-sm py-2 pl-2">No matching Allowed APIs found</p>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {(() => {
                        const items = [];
                        let lastGroup = null;
                        methodSteps.forEach((step, index) => {
                          const group = getApiGroup(step.apiPath, step.apiSpecTitle);
                          if (group !== lastGroup) {
                            lastGroup = group;
                            if (group) {
                              const [prefix, ...rest] = group.split(' > ');
                              const suffix = rest.join(' > ');
                              items.push(
                                <div key={`group-${group}-${index}`} className="flex items-center gap-2 mt-4 mb-1">
                                  <span className="text-heading-02 text-default">
                                    <span className="font-black">{prefix}</span>
                                    {suffix && <span className="font-normal"> &gt; {suffix}</span>}
                                  </span>
                                  <div className="flex-1 border-t border-action-default opacity-30" />
                                </div>
                              );
                            }
                          }
                          items.push(
                            <SdfExpandableBox
                              key={`${method}-${step.apiPath}-${index}`}
                              variant="shadowed"
                              triggerPlacement="after"
                              onSdfOpen={() => handleExpandOpen(method, step.apiPath)}
                            >
                              <div slot="header" className="flex items-center w-full">
                                <SdfBadge status={HttpMethodsToSdfEnum[method] ?? "default"}>
                                  {method}
                                </SdfBadge>
                                <div className="text-heading-04 font-bold ml-4 overflow-hidden truncate">
                                  {step.apiPath || step.integrationObjective || "—"}
                                </div>
                              </div>
                              {expandedKeys.has(`${method}::${step.apiPath}`) && (
                                <ApiItemDetails step={enrichStep({ ...step, httpMethod: method })} isSorFetching={isSorFetching} sorData={sorData} guideTitleMap={guideTitleMap} />
                              )}
                            </SdfExpandableBox>
                          );
                        });
                        return items;
                      })()}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Rows per page + Pagination */}
        {filteredSteps.length > 0 && (
          <div className="flex justify-end items-center mt-6 text-xs gap-2">
            <span className="text-tertiary">Rows per page</span>
            <SdfSelectSimple
              className="flex"
              inputWidth_md="1xs"
              size="sm"
              emphasis="tertiary"
              items={ROWS_PER_PAGE_OPTIONS}
              value={ROWS_PER_PAGE_OPTIONS.find((o) => o.value === String(pageSize))}
              onSdfChange={(e) => setPageSize(Number(e.detail.value))}
            />
            <span className="text-default text-sm px-2">|</span>
            <span className="text-tertiary whitespace-nowrap">
              {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredSteps.length)} of {filteredSteps.length}
            </span>
            {totalPages > 1 && (
              <SdfxPagination
                currentPage={String(currentPage)}
                totalPages={totalPages}
                onSdfxPageChange={(e) => setCurrentPage(Number(e.detail))}
              />
            )}
          </div>
        )}
          </>
        )}

        <sdf-button
          emphasis="secondary"
          slot="custom-buttons"
          onClick={() => handleOnHide()}
        >
          Close
        </sdf-button>
      </SdfFocusPane>
    </div>
  );
}

export default ClaraFeatureSteps;
